// src/swarm/context/SwarmContext.tsx
// Production Swarm Orchestration Context
// Backed by the real backend swarm API (src/api/swarm.ts) — no local simulation.

import React, { createContext, useContext, useReducer, useCallback, useRef, useEffect } from 'react';
import {
  AgentTask, AgentConfig, SwarmState, ParsedIntent, Artifact,
  SwarmMetrics, AgentStatus, AgentType,
} from '../types';
import { DAGEngine, validateTaskDependencies } from '../dagEngine';
import {
  createSwarm as apiCreateSwarm,
  executeSwarm as apiExecuteSwarm,
  getSwarmStatus as apiGetSwarmStatus,
  getSwarmResult as apiGetSwarmResult,
  cancelSwarm as apiCancelSwarm,
  SwarmStatusAgentRow,
} from '../../api/swarm';

// ═══════════════════════════════════════════════════════════════════════════
// STATE & ACTIONS
// ═══════════════════════════════════════════════════════════════════════════

interface SwarmContextState {
  swarms: Map<string, SwarmState>;
  activeSwarmId: string | null;
  isProcessing: boolean;
  globalMetrics: SwarmMetrics;
}

type SwarmAction =
  | { type: 'INIT_SWARM'; payload: { id: string; userRequest: string; intent: ParsedIntent; tasks: AgentTask[] } }
  | { type: 'SYNC_AGENTS'; payload: { swarmId: string; agents: AgentTask[] } }
  | { type: 'UPDATE_TASK'; payload: { swarmId: string; taskId: string; updates: Partial<AgentTask> } }
  | { type: 'UPDATE_SWARM_STATUS'; payload: { swarmId: string; status: SwarmState['status'] } }
  | { type: 'ADD_ARTIFACT'; payload: { swarmId: string; artifact: Artifact } }
  | { type: 'UPDATE_METRICS'; payload: { swarmId: string; metrics: Partial<SwarmMetrics> } }
  | { type: 'SET_ACTIVE_SWARM'; payload: { swarmId: string } }
  | { type: 'COMPLETE_SWARM'; payload: { swarmId: string; result: any } }
  | { type: 'SET_PROCESSING'; payload: boolean }
  | { type: 'SET_GLOBAL_METRICS'; payload: Partial<SwarmMetrics> };

const initialState: SwarmContextState = {
  swarms: new Map(),
  activeSwarmId: null,
  isProcessing: false,
  globalMetrics: {
    totalTasks: 0,
    completedTasks: 0,
    failedTasks: 0,
    activeAgents: 0,
    totalToolCalls: 0,
    averageTaskDuration: 0,
    totalCost: 0,
  },
};

function swarmReducer(state: SwarmContextState, action: SwarmAction): SwarmContextState {
  switch (action.type) {
    case 'INIT_SWARM': {
      const { id, userRequest, intent, tasks } = action.payload;
      const newSwarms = new Map(state.swarms);

      const validation = validateTaskDependencies(tasks);
      if (!validation.valid) {
        console.error('Invalid task dependencies:', validation.errors);
      }

      const dagEngine = new DAGEngine(tasks);
      const executionGraph = dagEngine.buildExecutionGraph();

      const swarmState: SwarmState = {
        id,
        userRequest,
        intent,
        executionGraph,
        tasks: new Map(tasks.map(t => [t.id, t])),
        agents: new Map(),
        sharedMemory: {
          globalContext: { objective: userRequest },
          agentOutputs: {},
          constraints: intent.constraints,
          artifacts: [],
        },
        status: 'planning',
        startTime: Date.now(),
        metrics: {
          totalTasks: tasks.length,
          completedTasks: 0,
          failedTasks: 0,
          activeAgents: 0,
          totalToolCalls: 0,
          averageTaskDuration: 0,
          totalCost: 0,
        },
      };

      newSwarms.set(id, swarmState);
      persistSwarm(swarmState);

      return { ...state, swarms: newSwarms, activeSwarmId: id };
    }

    case 'SYNC_AGENTS': {
      const { swarmId, agents } = action.payload;
      const newSwarms = new Map(state.swarms);
      const swarm = newSwarms.get(swarmId);
      if (!swarm) return state;

      agents.forEach(row => {
        const existing = swarm.tasks.get(row.id);
        const updated: AgentTask = existing ? { ...existing, ...row } : row;

        if (updated.status === 'working' && !updated.startTime) {
          updated.startTime = Date.now();
        }
        if (updated.status === 'done' && !updated.endTime) {
          updated.endTime = Date.now();
        }

        swarm.tasks.set(row.id, updated);

        if (updated.outputs && Object.keys(updated.outputs).length > 0) {
          swarm.sharedMemory.agentOutputs[row.id] = updated.outputs;
        }
      });

      const allTasks = Array.from(swarm.tasks.values());
      const doneCount = allTasks.filter(t => t.status === 'done').length;
      const errorCount = allTasks.filter(t => t.status === 'error').length;
      const workingCount = allTasks.filter(t => t.status === 'working').length;

      swarm.metrics = {
        ...swarm.metrics,
        totalTasks: allTasks.length,
        completedTasks: doneCount,
        failedTasks: errorCount,
        activeAgents: workingCount,
      };

      persistSwarm(swarm);
      return { ...state, swarms: newSwarms };
    }

    case 'UPDATE_TASK': {
      const { swarmId, taskId, updates } = action.payload;
      const newSwarms = new Map(state.swarms);
      const swarm = newSwarms.get(swarmId);
      if (!swarm) return state;

      const task = swarm.tasks.get(taskId);
      if (!task) return state;

      const updatedTask = { ...task, ...updates };
      if (updates.status === 'working' && !task.startTime) updatedTask.startTime = Date.now();
      if (updates.status === 'done' && !task.endTime) updatedTask.endTime = Date.now();

      swarm.tasks.set(taskId, updatedTask);

      const doneCount = Array.from(swarm.tasks.values()).filter(t => t.status === 'done').length;
      const errorCount = Array.from(swarm.tasks.values()).filter(t => t.status === 'error').length;
      const workingCount = Array.from(swarm.tasks.values()).filter(t => t.status === 'working').length;

      swarm.metrics = {
        ...swarm.metrics,
        completedTasks: doneCount,
        failedTasks: errorCount,
        activeAgents: workingCount,
      };

      if (updates.outputs && Object.keys(updates.outputs).length > 0) {
        swarm.sharedMemory.agentOutputs[taskId] = updates.outputs;
      }
      if (updates.logs && updates.logs.length > 0) {
        updatedTask.logs = [...task.logs, ...updates.logs];
      }

      persistSwarm(swarm);
      return { ...state, swarms: newSwarms };
    }

    case 'UPDATE_SWARM_STATUS': {
      const { swarmId, status } = action.payload;
      const newSwarms = new Map(state.swarms);
      const swarm = newSwarms.get(swarmId);
      if (!swarm) return state;

      swarm.status = status;
      if (status === 'completed' || status === 'failed') {
        swarm.endTime = Date.now();
      }

      persistSwarm(swarm);
      return { ...state, swarms: newSwarms };
    }

    case 'ADD_ARTIFACT': {
      const { swarmId, artifact } = action.payload;
      const newSwarms = new Map(state.swarms);
      const swarm = newSwarms.get(swarmId);
      if (!swarm) return state;

      swarm.sharedMemory.artifacts.push(artifact);
      persistSwarm(swarm);
      return { ...state, swarms: newSwarms };
    }

    case 'UPDATE_METRICS': {
      const { swarmId, metrics } = action.payload;
      const newSwarms = new Map(state.swarms);
      const swarm = newSwarms.get(swarmId);
      if (!swarm) return state;

      swarm.metrics = { ...swarm.metrics, ...metrics };
      persistSwarm(swarm);
      return { ...state, swarms: newSwarms };
    }

    case 'SET_ACTIVE_SWARM':
      return { ...state, activeSwarmId: action.payload.swarmId };

    case 'COMPLETE_SWARM': {
      const { swarmId } = action.payload;
      const newSwarms = new Map(state.swarms);
      const swarm = newSwarms.get(swarmId);

      if (swarm) {
        swarm.status = 'completed';
        swarm.endTime = Date.now();
        persistSwarm(swarm);
      }

      return { ...state, swarms: newSwarms, isProcessing: false };
    }

    case 'SET_PROCESSING':
      return { ...state, isProcessing: action.payload };

    case 'SET_GLOBAL_METRICS':
      return { ...state, globalMetrics: { ...state.globalMetrics, ...action.payload } };

    default:
      return state;
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// LOCALSTORAGE PERSISTENCE
// ═══════════════════════════════════════════════════════════════════════════

const SWARM_STORAGE_KEY = 'miraal_swarm_states';

function persistSwarm(swarm: SwarmState): void {
  try {
    const stored = localStorage.getItem(SWARM_STORAGE_KEY);
    const allSwarms: Record<string, any> = stored ? JSON.parse(stored) : {};

    allSwarms[swarm.id] = {
      ...swarm,
      tasks: Object.fromEntries(swarm.tasks),
      agents: Object.fromEntries(swarm.agents),
      executionGraph: {
        ...swarm.executionGraph,
        nodes: Object.fromEntries(swarm.executionGraph.nodes),
      },
    };

    localStorage.setItem(SWARM_STORAGE_KEY, JSON.stringify(allSwarms));
  } catch (e) {
    console.warn('Failed to persist swarm:', e);
  }
}

function loadSwarmsFromStorage(): Map<string, SwarmState> {
  try {
    const stored = localStorage.getItem(SWARM_STORAGE_KEY);
    if (!stored) return new Map();

    const parsed = JSON.parse(stored);
    const swarms = new Map<string, SwarmState>();

    for (const [id, data] of Object.entries(parsed)) {
      const swarmData = data as any;
      swarms.set(id, {
        ...swarmData,
        tasks: new Map(Object.entries(swarmData.tasks || {})),
        agents: new Map(Object.entries(swarmData.agents || {})),
        executionGraph: {
          ...swarmData.executionGraph,
          nodes: new Map(Object.entries(swarmData.executionGraph?.nodes || {})),
        },
      });
    }

    return swarms;
  } catch (e) {
    console.warn('Failed to load swarms:', e);
    return new Map();
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// BACKEND ROW -> AgentTask MAPPING
// ═══════════════════════════════════════════════════════════════════════════

const BACKEND_STATUS_MAP: Record<string, AgentStatus> = {
  idle: 'idle',
  working: 'working',
  done: 'done',       // real backend value
  complete: 'done',   // tolerate alternate spellings just in case
  completed: 'done',
  error: 'error',
};

// Maps the real DAG node role (plan/copy/image/code/qa/assemble, from
// CELERY_AGENT_MAP on the backend) to the icon/color key AgentSwarmPage's
// AGENT_ICONS/AGENT_COLORS maps expect.
const ROLE_TO_AGENT_TYPE: Record<string, AgentType> = {
  plan: 'research',
  copy: 'docs',
  image: 'design',
  image_ideogram: 'design',
  code: 'code',
  qa: 'test',
  assemble: 'deploy',
};

function mapAgentRow(row: SwarmStatusAgentRow): AgentTask {
  const rawStatus = String(row.status || 'idle');
  const status: AgentStatus = BACKEND_STATUS_MAP[rawStatus] || 'idle';

  return {
    id: row.agent_id,
    agentType: ROLE_TO_AGENT_TYPE[row.role] || 'research',
    title: row.agent_name,
    description: row.agent_name,
    priority: 'normal',
    dependsOn: row.depends_on || [],
    inputs: {},
    outputs: row.output ? { result: row.output } : {},
    status,
    progress: status === 'done' ? 100 : status === 'working' ? 50 : 0,
    errorCount: 0,
    logs: row.output ? [row.output.slice(0, 300)] : [],
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// CONTEXT DEFINITION
// ═══════════════════════════════════════════════════════════════════════════

interface SwarmContextValue {
  state: SwarmContextState;
  dispatch: React.Dispatch<SwarmAction>;
  createSwarm: (userRequest: string) => Promise<string>;
  executeSwarm: (swarmId: string) => Promise<void>;
  cancelSwarm: (swarmId: string) => void;
  retryTask: (swarmId: string, taskId: string) => void;
  getSwarmStatus: (swarmId: string) => SwarmState | undefined;
  getActiveTasks: (swarmId: string) => AgentTask[];
  getReadyTasks: (swarmId: string) => AgentTask[];
  getVisualizationData: (swarmId: string) => any;
}

const SwarmContext = createContext<SwarmContextValue | null>(null);

// ═══════════════════════════════════════════════════════════════════════════
// PROVIDER
// ═══════════════════════════════════════════════════════════════════════════

const POLL_INTERVAL_MS = 1500;

export const SwarmProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(swarmReducer, initialState, (init) => ({
    ...init,
    swarms: loadSwarmsFromStorage(),
  }));

  const pollIntervals = useRef<Map<string, ReturnType<typeof setInterval>>>(new Map());

  useEffect(() => {
    return () => {
      for (const interval of pollIntervals.current.values()) {
        clearInterval(interval);
      }
    };
  }, []);

  const clearPoll = useCallback((swarmId: string) => {
    const interval = pollIntervals.current.get(swarmId);
    if (interval) {
      clearInterval(interval);
      pollIntervals.current.delete(swarmId);
    }
  }, []);

  /**
   * Create a new swarm task on the real backend.
   */
  const createSwarm = useCallback(async (userRequest: string): Promise<string> => {
    dispatch({ type: 'SET_PROCESSING', payload: true });

    try {
      const response = await apiCreateSwarm(userRequest);
      const swarmId = response.id;

      const intent: ParsedIntent = {
        category: 'general',
        objective: userRequest,
        constraints: [],
        estimatedComplexity: 'moderate',
        requiredAgentTypes: [],
        suggestedTools: [],
      };

      // Tasks start empty — the real agent list arrives once we poll status after execute.
      dispatch({
        type: 'INIT_SWARM',
        payload: { id: swarmId, userRequest, intent, tasks: [] },
      });

      return swarmId;
    } catch (error) {
      console.error('Failed to create swarm:', error);
      dispatch({ type: 'SET_PROCESSING', payload: false });
      throw error;
    }
  }, []);

  /**
   * Kick off real execution on the backend, then poll status until done.
   */
  const executeSwarm = useCallback((swarmId: string): Promise<void> => {
    return new Promise<void>((resolve, reject) => {
      const finishWithError = (status: SwarmState['status'], error: Error) => {
        clearPoll(swarmId);
        dispatch({ type: 'UPDATE_SWARM_STATUS', payload: { swarmId, status } });
        dispatch({ type: 'SET_PROCESSING', payload: false });
        reject(error);
      };

      const poll = async () => {
        try {
          const statusRes = await apiGetSwarmStatus(swarmId);
          const tasks: AgentTask[] = statusRes.agents.map((row) => mapAgentRow(row));

          dispatch({ type: 'SYNC_AGENTS', payload: { swarmId, agents: tasks } });

          if (statusRes.status === 'completed') {
            clearPoll(swarmId);
            dispatch({ type: 'UPDATE_SWARM_STATUS', payload: { swarmId, status: 'synthesizing' } });

            try {
              const result = await apiGetSwarmResult(swarmId);
              if (result.result) {
                dispatch({
                  type: 'ADD_ARTIFACT',
                  payload: {
                    swarmId,
                    artifact: {
                      id: `synthesis_${Date.now()}`,
                      type: 'document',
                      producer: 'synthesis_engine',
                      content: result.result,
                      metadata: {},
                      timestamp: Date.now(),
                    },
                  },
                });
              }
            } catch (e) {
              console.warn('Failed to fetch swarm result:', e);
            }

            dispatch({ type: 'COMPLETE_SWARM', payload: { swarmId, result: null } });
            resolve();
          } else if (statusRes.status === 'failed' || statusRes.status === 'cancelled') {
            finishWithError('failed', new Error(`Swarm ${statusRes.status}`));
          }
          // status 'pending' / 'running' -> keep polling
        } catch (error) {
          console.error(`Polling failed for swarm ${swarmId}:`, error);
          finishWithError('failed', error as Error);
        }
      };

      dispatch({ type: 'UPDATE_SWARM_STATUS', payload: { swarmId, status: 'executing' } });

      apiExecuteSwarm(swarmId)
        .then(() => {
          const interval = setInterval(poll, POLL_INTERVAL_MS);
          pollIntervals.current.set(swarmId, interval);
          poll(); // fire immediately, don't wait for the first tick
        })
        .catch((error) => {
          console.error(`Failed to start swarm ${swarmId}:`, error);
          finishWithError('failed', error as Error);
        });
    });
  }, [clearPoll]);

  /**
   * Cancel a running swarm.
   */
  const cancelSwarm = useCallback((swarmId: string) => {
    clearPoll(swarmId);
    apiCancelSwarm(swarmId).catch((e) => console.warn('Cancel request failed:', e));
    dispatch({ type: 'UPDATE_SWARM_STATUS', payload: { swarmId, status: 'failed' } });
    dispatch({ type: 'SET_PROCESSING', payload: false });
  }, [clearPoll]);

  /**
   * Per-task retry has no backend equivalent — the orchestrator already retries
   * each agent automatically (2 retries, exponential backoff) before marking it
   * 'error'. This is a no-op so the UI button doesn't break, but it won't
   * re-trigger anything on the server.
   */
  const retryTask = useCallback((_swarmId: string, taskId: string) => {
    console.warn(
      `Retry requested for task ${taskId}, but the backend already auto-retries failed agents. No action taken.`
    );
  }, []);

  const getSwarmStatus = useCallback((swarmId: string) => {
    return state.swarms.get(swarmId);
  }, [state.swarms]);

  const getActiveTasks = useCallback((swarmId: string) => {
    const swarm = state.swarms.get(swarmId);
    if (!swarm) return [];
    return Array.from(swarm.tasks.values()).filter(t => t.status === 'working');
  }, [state.swarms]);

  const getReadyTasks = useCallback((swarmId: string) => {
    const swarm = state.swarms.get(swarmId);
    if (!swarm) return [];

    const completed = new Set(
      Array.from(swarm.tasks.values()).filter(t => t.status === 'done').map(t => t.id)
    );

    const dagEngine = new DAGEngine(Array.from(swarm.tasks.values()));
    const readyIds = dagEngine.getReadyTasks(completed);

    return readyIds.map(id => swarm.tasks.get(id)).filter(Boolean) as AgentTask[];
  }, [state.swarms]);

  const getVisualizationData = useCallback((swarmId: string) => {
    const swarm = state.swarms.get(swarmId);
    if (!swarm) return null;

    const dagEngine = new DAGEngine(Array.from(swarm.tasks.values()));
    return dagEngine.serializeForVisualization();
  }, [state.swarms]);

  const value: SwarmContextValue = {
    state,
    dispatch,
    createSwarm,
    executeSwarm,
    cancelSwarm,
    retryTask,
    getSwarmStatus,
    getActiveTasks,
    getReadyTasks,
    getVisualizationData,
  };

  return (
    <SwarmContext.Provider value={value}>
      {children}
    </SwarmContext.Provider>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// HOOKS
// ═══════════════════════════════════════════════════════════════════════════

export const useSwarm = () => {
  const context = useContext(SwarmContext);
  if (!context) {
    throw new Error('useSwarm must be used within a SwarmProvider');
  }
  return context;
};

export const useActiveSwarm = () => {
  const { state, getSwarmStatus, getActiveTasks, getReadyTasks, getVisualizationData } = useSwarm();
  const activeSwarm = state.activeSwarmId ? getSwarmStatus(state.activeSwarmId) : undefined;

  return {
    swarm: activeSwarm,
    isProcessing: state.isProcessing,
    activeTasks: state.activeSwarmId ? getActiveTasks(state.activeSwarmId) : [],
    readyTasks: state.activeSwarmId ? getReadyTasks(state.activeSwarmId) : [],
    visualizationData: state.activeSwarmId ? getVisualizationData(state.activeSwarmId) : null,
    metrics: activeSwarm?.metrics || state.globalMetrics,
  };
};
